import api from './client';

/**
 * Quota prediction for subscription changes
 */
export interface QuotaPrediction {
  will_exceed_quotas: boolean;
  overages: Array<{
    resource: string;
    current: number;
    limit: number;
    over_by: number;
    message: string;
  }>;
  current_usage: {
    projects: number;
    architectures: number;
    deployments_this_month?: number;
  };
  target_limits: {
    projects: number;
    architectures: number;
    deployments_per_month?: number;
  };
}

/**
 * Create a Stripe Checkout session for upgrading to a new plan
 * OR update existing subscription
 */
export async function createCheckoutSession(
  targetPlan: string,
  billingCycle: 'monthly' | 'yearly',
  successUrl: string,
  cancelUrl: string,
  promoCode?: string,
  currency?: string
): Promise<
  | { sessionId: string; url: string; success?: never; quota_prediction?: never }
  | { success: true; message: string; subscription: any; url?: never; quota_prediction?: QuotaPrediction }
> {
  const payload: any = {
    target_plan: targetPlan,
    billing_cycle: billingCycle,
    success_url: successUrl,
    cancel_url: cancelUrl,
  };

  if (promoCode) {
    payload.promo_code = promoCode;
  }

  if (currency) {
    payload.currency = currency;
  }

  const response = await api.post('/api/payments/create-checkout-session', payload);
  return response.data;
}

/**
 * Create a Stripe Customer Portal session for managing subscription
 */
export async function createCustomerPortalSession(
  returnUrl: string
): Promise<{ url: string }> {
  const response = await api.post('/api/payments/create-portal-session', {
    return_url: returnUrl,
  });
  return response.data;
}

/**
 * Cancel the current subscription (effective at period end)
 */
export async function previewCancellation(): Promise<{
  success: boolean;
  quota_prediction: QuotaPrediction;
  current_plan: string;
  target_plan: string;
  effective_date: string | null;
}> {
  const response = await api.post('/api/payments/preview-cancellation');
  return response.data;
}

export async function cancelSubscription(): Promise<{
  success: boolean;
  message: string;
  effective_date?: string;
  quota_prediction?: QuotaPrediction;
}> {
  const response = await api.post('/api/payments/cancel-subscription');
  return response.data;
}

/**
 * Get customer invoices from Stripe
 */
export interface Invoice {
  id: string;
  number: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
}

export async function getInvoices(limit: number = 10): Promise<{ invoices: Invoice[] }> {
  const response = await api.get(`/api/payments/invoices?limit=${limit}`);
  return response.data;
}

/**
 * Preview proration for an upgrade
 */
export interface ProrationDetails {
  current_plan: string;
  current_plan_price: number;
  target_plan: string;
  target_plan_price: number;
  billing_cycle: string;
  prorated_credit: number;
  prorated_charge: number;
  total_due_now: number;
  currency: string;
  period_start: string;
  period_end: string;
  remaining_days: number;
  total_days: number;
  immediate_charge: boolean;
}

export async function previewProration(
  targetPlan: string,
  billingCycle: 'monthly' | 'yearly'
): Promise<{ success: boolean; proration: ProrationDetails }> {
  const response = await api.post('/api/payments/preview-proration', {
    target_plan: targetPlan,
    billing_cycle: billingCycle,
  });
  return response.data;
}

/**
 * Submit cancellation feedback with reason
 */
export async function submitCancellationFeedback(
  reason: string,
  feedback: string
): Promise<{ success: boolean }> {
  const response = await api.post('/api/payments/cancellation-feedback', {
    reason,
    feedback,
  });
  return response.data;
}

/**
 * Check retention offer eligibility
 */
export interface RetentionEligibility {
  eligible: boolean;
  reason?: string;
  last_offer_type?: string;
  last_offer_date?: string;
  message?: string;
}

export async function checkRetentionEligibility(): Promise<RetentionEligibility> {
  const response = await api.get('/api/payments/retention-eligibility');
  return response.data;
}

/**
 * Apply retention offer (discount, pause, or downgrade)
 */
export interface RetentionOfferResponse {
  success: boolean;
  message: string;
  offer: {
    offer_type: 'discount' | 'pause' | 'downgrade';
    discount_percent?: number;
    pause_days?: number;
    target_plan?: string;
    applied_at: string;
    expires_at?: string;
  };
}

export async function applyRetentionOffer(
  offerType: 'discount' | 'pause' | 'downgrade'
): Promise<RetentionOfferResponse> {
  const response = await api.post('/api/payments/retention-offer', {
    offer_type: offerType,
  });
  return response.data;
}
